import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Chip,
  Box,
  Alert,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Tooltip,
  TextField,
  InputAdornment,
  Grid,
  FormControlLabel,
  Switch,
  Card,
  CardContent,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Divider,
  Badge
} from '@mui/material';
import {
  Edit,
  Delete,
  Archive,
  Visibility,
  Search,
  Pause,
  PlayArrow,
  Work,
  Settings,
  Assignment,
  ArrowUpward,
  ArrowDownward,
  Add,
  Timeline
} from '@mui/icons-material';
import axios from 'axios';

const ManageJobs = () => {
  const navigate = useNavigate();
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [deleteDialog, setDeleteDialog] = useState({ open: false, job: null });
  const [statusDialog, setStatusDialog] = useState({ open: false, job: null, newStatus: '' });
  const [processDialog, setProcessDialog] = useState({ open: false, job: null });
  const [selectedJob, setSelectedJob] = useState(null);
  const [selectionSettings, setSelectionSettings] = useState({
    skipScreening: false,
    skipAssessment: false,
    skipInterview: false,
    activeStages: []
  });
  const [linkedTasks, setLinkedTasks] = useState([]);

  useEffect(() => {
    fetchJobs();
  }, []);

  const fetchJobs = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const response = await axios.get('http://localhost:5000/api/jobs', {
        params: { limit: 100 },
        headers: { Authorization: `Bearer ${token}` }
      });
      setJobs(response.data.data || []);
    } catch (err) {
      console.error('Error fetching jobs:', err);
      setError('Failed to load jobs');
    } finally {
      setLoading(false);
    }
  };

  const fetchLinkedTasks = async (jobId) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`http://localhost:5000/api/tasks`, {
        params: { jobId },
        headers: { Authorization: `Bearer ${token}` }
      });
      return response.data.data || [];
    } catch (err) {
      console.error('Error fetching tasks:', err);
      return [];
    }
  };

  const handleEditJob = (jobId) => {
    navigate(`/jobs/${jobId}/edit`);
  };

  const handleViewJob = (jobId) => {
    navigate(`/jobs/${jobId}`);
  };

  const handleStatusChange = async (job, newStatus) => {
    setStatusDialog({ open: true, job, newStatus });
  };

  const confirmStatusChange = async () => {
    try {
      const token = localStorage.getItem('token');
      await axios.put(
        `http://localhost:5000/api/jobs/${statusDialog.job._id}`,
        { status: statusDialog.newStatus },
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      
      // Update local state
      setJobs(jobs.map(j => 
        j._id === statusDialog.job._id 
          ? { ...j, status: statusDialog.newStatus }
          : j
      ));
      
      setStatusDialog({ open: false, job: null, newStatus: '' });
    } catch (err) {
      console.error('Error updating job status:', err);
      setError('Failed to update job status');
    }
  };

  const handleProcessSettings = async (job) => {
    setSelectedJob(job);
    const tasks = await fetchLinkedTasks(job._id);
    setLinkedTasks(tasks);
    setSelectionSettings({
      skipScreening: job.selectionProcess?.skipScreening || false,
      skipAssessment: job.selectionProcess?.skipAssessment || false,
      skipInterview: job.selectionProcess?.skipInterview || false,
      activeStages: job.selectionProcess?.activeStages || ['Applied', 'Screening', 'Technical Assessment', 'Interview', 'Offer', 'Hired']
    });
    setProcessDialog({ open: true, job });
  };

  const saveProcessSettings = async () => {
    try {
      const token = localStorage.getItem('token');
      await axios.put(
        `http://localhost:5000/api/jobs/${selectedJob._id}`,
        { 
          selectionProcess: selectionSettings 
        },
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      
      // Update local state
      setJobs(jobs.map(j => 
        j._id === selectedJob._id 
          ? { ...j, selectionProcess: selectionSettings }
          : j
      ));
      
      setProcessDialog({ open: false, job: null });
      setSelectedJob(null);
    } catch (err) {
      console.error('Error updating selection process:', err);
      setError('Failed to update selection process');
    }
  };

  const handleStageToggle = (stage) => {
    setSelectionSettings(prev => {
      const activeStages = prev.activeStages.includes(stage)
        ? prev.activeStages.filter(s => s !== stage)
        : [...prev.activeStages, stage];
      
      // Auto-skip stages if they're disabled
      const skipScreening = !activeStages.includes('Screening');
      const skipAssessment = !activeStages.includes('Technical Assessment');
      const skipInterview = !activeStages.includes('Interview');
      
      return {
        ...prev,
        activeStages,
        skipScreening,
        skipAssessment,
        skipInterview
      };
    });
  };

  const handleArchiveJob = async (job) => {
    setDeleteDialog({ open: true, job });
  };

  const confirmArchive = async () => {
    try {
      const token = localStorage.getItem('token');
      await axios.delete(
        `http://localhost:5000/api/jobs/${deleteDialog.job._id}`,
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      
      // Remove from local state
      setJobs(jobs.filter(j => j._id !== deleteDialog.job._id));
      setDeleteDialog({ open: false, job: null });
    } catch (err) {
      console.error('Error archiving job:', err);
      setError('Failed to archive job');
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      draft: 'default',
      active: 'success',
      paused: 'warning',
      closed: 'error',
      cancelled: 'default'
    };
    return colors[status] || 'default';
  };

  const formatDate = (date) => {
    return new Date(date).toLocaleDateString('en-IN', {
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    });
  };

  const formatSalary = (salaryRange) => {
    if (!salaryRange || !salaryRange.min || !salaryRange.max) return 'Not Specified';
    const formatter = new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    });
    return `${formatter.format(salaryRange.min)} - ${formatter.format(salaryRange.max)}`;
  };

  const getActiveStagesCount = (job) => {
    if (!job.selectionProcess?.activeStages) return 6; // Default stages
    return job.selectionProcess.activeStages.length;
  };

  const filteredJobs = jobs.filter(job =>
    job.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
    job.department.toLowerCase().includes(searchTerm.toLowerCase()) ||
    job.location.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) {
    return (
      <Container sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress />
      </Container>
    );
  }

  return (
    <Container maxWidth="lg">
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4">
          Manage Jobs
        </Typography>
        <Button
          variant="contained"
          startIcon={<Work />}
          onClick={() => navigate('/create-job')}
        >
          Create New Job
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Box sx={{ mb: 3 }}>
        <TextField
          fullWidth
          placeholder="Search jobs by title, department, or location..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search />
              </InputAdornment>
            ),
          }}
        />
      </Box>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Job Title</TableCell>
              <TableCell>Department</TableCell>
              <TableCell>Location</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Process</TableCell>
              <TableCell>Openings</TableCell>
              <TableCell>Applications</TableCell>
              <TableCell>Deadline</TableCell>
              <TableCell align="center">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredJobs.map((job) => (
              <TableRow key={job._id}>
                <TableCell>
                  <Typography variant="subtitle2">{job.title}</Typography>
                  <Typography variant="caption" color="text.secondary">
                    {job.experienceLevel}
                  </Typography>
                </TableCell>
                <TableCell>{job.department}</TableCell>
                <TableCell>
                  {job.location}
                  <br />
                  <Typography variant="caption" color="text.secondary">
                    {job.workMode}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Chip
                    label={job.status}
                    color={getStatusColor(job.status)}
                    size="small"
                  />
                </TableCell>
                <TableCell>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Badge badgeContent={getActiveStagesCount(job)} color="primary">
                      <Timeline fontSize="small" />
                    </Badge>
                    <Tooltip title="Configure Selection Process">
                      <IconButton
                        size="small"
                        color="primary"
                        onClick={() => handleProcessSettings(job)}
                      >
                        <Settings fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </Box>
                </TableCell>
                <TableCell>
                  {job.filledPositions || 0}/{job.openings}
                </TableCell>
                <TableCell>{job.analytics?.applications || 0}</TableCell>
                <TableCell>
                  {formatDate(job.applicationDeadline)}
                  {new Date(job.applicationDeadline) > new Date(new Date().setFullYear(new Date().getFullYear() + 1)) && (
                    <Typography variant="caption" display="block" color="text.secondary">
                      (Rolling)
                    </Typography>
                  )}
                </TableCell>
                <TableCell align="center">
                  <Box sx={{ display: 'flex', gap: 0.5 }}>
                    <Tooltip title="View Details">
                      <IconButton
                        size="small"
                        color="primary"
                        onClick={() => handleViewJob(job._id)}
                      >
                        <Visibility />
                      </IconButton>
                    </Tooltip>
                    
                    <Tooltip title="Edit Job">
                      <IconButton
                        size="small"
                        color="primary"
                        onClick={() => handleEditJob(job._id)}
                      >
                        <Edit />
                      </IconButton>
                    </Tooltip>

                    {job.status === 'active' ? (
                      <Tooltip title="Pause Job">
                        <IconButton
                          size="small"
                          color="warning"
                          onClick={() => handleStatusChange(job, 'paused')}
                        >
                          <Pause />
                        </IconButton>
                      </Tooltip>
                    ) : job.status === 'paused' ? (
                      <Tooltip title="Activate Job">
                        <IconButton
                          size="small"
                          color="success"
                          onClick={() => handleStatusChange(job, 'active')}
                        >
                          <PlayArrow />
                        </IconButton>
                      </Tooltip>
                    ) : null}

                    <Tooltip title="Archive Job">
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() => handleArchiveJob(job)}
                      >
                        <Archive />
                      </IconButton>
                    </Tooltip>
                  </Box>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        {filteredJobs.length === 0 && (
          <Box sx={{ py: 4, textAlign: 'center' }}>
            <Typography color="text.secondary">
              No jobs found
            </Typography>
          </Box>
        )}
      </TableContainer>

      {/* Selection Process Configuration Dialog */}
      <Dialog 
        open={processDialog.open} 
        onClose={() => setProcessDialog({ open: false, job: null })}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          Configure Selection Process - {selectedJob?.title}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={3} sx={{ mt: 1 }}>
            <Grid item xs={12} md={6}>
              <Card variant="outlined">
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Selection Stages
                  </Typography>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    Toggle stages to include/skip them in the hiring process
                  </Typography>
                  <Divider sx={{ my: 2 }} />
                  
                  <List>
                    <ListItem>
                      <ListItemText 
                        primary="Applied"
                        secondary="Initial application (always active)"
                      />
                      <ListItemSecondaryAction>
                        <Switch checked disabled />
                      </ListItemSecondaryAction>
                    </ListItem>
                    
                    <ListItem>
                      <ListItemText 
                        primary="Screening"
                        secondary="Resume and profile review"
                      />
                      <ListItemSecondaryAction>
                        <Switch 
                          checked={selectionSettings.activeStages.includes('Screening')}
                          onChange={() => handleStageToggle('Screening')}
                        />
                      </ListItemSecondaryAction>
                    </ListItem>
                    
                    <ListItem>
                      <ListItemText 
                        primary="Technical Assessment"
                        secondary="Skills tests and tasks"
                      />
                      <ListItemSecondaryAction>
                        <Switch 
                          checked={selectionSettings.activeStages.includes('Technical Assessment')}
                          onChange={() => handleStageToggle('Technical Assessment')}
                        />
                      </ListItemSecondaryAction>
                    </ListItem>
                    
                    <ListItem>
                      <ListItemText 
                        primary="Interview"
                        secondary="Interview rounds"
                      />
                      <ListItemSecondaryAction>
                        <Switch 
                          checked={selectionSettings.activeStages.includes('Interview')}
                          onChange={() => handleStageToggle('Interview')}
                        />
                      </ListItemSecondaryAction>
                    </ListItem>
                    
                    <ListItem>
                      <ListItemText 
                        primary="Offer"
                        secondary="Job offer stage"
                      />
                      <ListItemSecondaryAction>
                        <Switch 
                          checked={selectionSettings.activeStages.includes('Offer')}
                          onChange={() => handleStageToggle('Offer')}
                        />
                      </ListItemSecondaryAction>
                    </ListItem>
                    
                    <ListItem>
                      <ListItemText 
                        primary="Hired"
                        secondary="Final hiring (always active)"
                      />
                      <ListItemSecondaryAction>
                        <Switch checked disabled />
                      </ListItemSecondaryAction>
                    </ListItem>
                  </List>
                </CardContent>
              </Card>
            </Grid>
            
            <Grid item xs={12} md={6}>
              <Card variant="outlined">
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Linked Tasks
                  </Typography>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    Tasks assigned for technical assessment
                  </Typography>
                  <Divider sx={{ my: 2 }} />
                  
                  {linkedTasks.length > 0 ? (
                    <List>
                      {linkedTasks.map((task) => (
                        <ListItem key={task._id}>
                          <ListItemText 
                            primary={task.title}
                            secondary={`Duration: ${task.duration} mins | Type: ${task.type}`}
                          />
                        </ListItem>
                      ))}
                    </List>
                  ) : (
                    <Box sx={{ textAlign: 'center', py: 2 }}>
                      <Typography color="text.secondary">
                        No tasks linked to this job
                      </Typography>
                      <Button
                        startIcon={<Add />}
                        sx={{ mt: 2 }}
                        onClick={() => navigate('/tasks/create')}
                      >
                        Create Task
                      </Button>
                    </Box>
                  )}
                  
                  <Box sx={{ mt: 2 }}>
                    <Button
                      fullWidth
                      variant="outlined"
                      startIcon={<Assignment />}
                      onClick={() => navigate('/tasks')}
                    >
                      Manage Tasks
                    </Button>
                  </Box>
                </CardContent>
              </Card>
              
              <Card variant="outlined" sx={{ mt: 2 }}>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Process Summary
                  </Typography>
                  <Divider sx={{ my: 2 }} />
                  
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                    <Typography variant="body2">
                      Active Stages: <strong>{selectionSettings.activeStages.length}</strong>
                    </Typography>
                    {selectionSettings.skipScreening && (
                      <Alert severity="info" sx={{ py: 0.5 }}>
                        Screening stage will be skipped
                      </Alert>
                    )}
                    {selectionSettings.skipAssessment && (
                      <Alert severity="info" sx={{ py: 0.5 }}>
                        Technical assessment will be skipped
                      </Alert>
                    )}
                    {selectionSettings.skipInterview && (
                      <Alert severity="info" sx={{ py: 0.5 }}>
                        Interview stage will be skipped
                      </Alert>
                    )}
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setProcessDialog({ open: false, job: null })}>
            Cancel
          </Button>
          <Button onClick={saveProcessSettings} variant="contained">
            Save Configuration
          </Button>
        </DialogActions>
      </Dialog>

      {/* Status Change Dialog */}
      <Dialog open={statusDialog.open} onClose={() => setStatusDialog({ open: false, job: null, newStatus: '' })}>
        <DialogTitle>Change Job Status</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to change the status of "{statusDialog.job?.title}" to {statusDialog.newStatus}?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setStatusDialog({ open: false, job: null, newStatus: '' })}>
            Cancel
          </Button>
          <Button onClick={confirmStatusChange} variant="contained">
            Confirm
          </Button>
        </DialogActions>
      </Dialog>

      {/* Archive Dialog */}
      <Dialog open={deleteDialog.open} onClose={() => setDeleteDialog({ open: false, job: null })}>
        <DialogTitle>Archive Job</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to archive "{deleteDialog.job?.title}"? 
            This will remove it from active listings but preserve all application data.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialog({ open: false, job: null })}>
            Cancel
          </Button>
          <Button onClick={confirmArchive} color="error" variant="contained">
            Archive Job
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default ManageJobs;
